import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import {
  WITHDRAWAL_OUTCOMES,
  WithdrawalOutcome,
} from '@/server-functions/withdrawal/withdrawal-types';

export type WithdrawalStage = 'request' | 'confirm' | 'success';

// The withdrawal page carries all three tiles; the query string decides which one renders.
export function getWithdrawalStage(
  params: URLSearchParams | ReadonlyURLSearchParams | null,
): WithdrawalStage {
  if (params?.get('token')) {
    return 'confirm';
  }

  if (params?.get('status') === 'success') {
    return 'success';
  }

  return 'request';
}

const KNOWN_WITHDRAWAL_OUTCOMES: ReadonlySet<string> = new Set<string>(WITHDRAWAL_OUTCOMES);

function isWithdrawalOutcome(value: string | null | undefined): value is WithdrawalOutcome {
  return Boolean(value && KNOWN_WITHDRAWAL_OUTCOMES.has(value));
}

// Query string of the success stage; getWithdrawalStage and getWithdrawalOutcome read it back.
export function buildWithdrawalSuccessSearchParams(outcome?: WithdrawalOutcome): URLSearchParams {
  const params = new URLSearchParams({ status: 'success' });
  if (outcome) {
    params.set('outcome', outcome);
  }

  return params;
}

// The URL is user-editable, so only the known outcomes pass; anything else is undefined.
export function getWithdrawalOutcome(
  params: URLSearchParams | ReadonlyURLSearchParams | null,
): WithdrawalOutcome | undefined {
  const outcome = params?.get('outcome');
  return isWithdrawalOutcome(outcome) ? outcome : undefined;
}

export function useWithdrawalStage(): {
  stage: WithdrawalStage;
  token: string | null;
  outcome: WithdrawalOutcome | undefined;
} {
  const searchParams = useSearchParams();

  return {
    stage: getWithdrawalStage(searchParams),
    token: searchParams?.get('token') || null,
    outcome: getWithdrawalOutcome(searchParams),
  };
}
